import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Brain, Sparkles, Target, BookOpenCheck, ArrowRight, Play, Network, Lock, FileText, Clock, CheckCircle2, XCircle, Timer, Zap, Crown, Check } from "lucide-react";
import { api } from "@/lib/api";
import AnalogyPlayground from "@/components/AnalogyPlayground";
import TopNav from "@/components/TopNav";

function HeroCarousel({ onPlay }) {
  const [images, setImages] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // Public endpoint — no auth required
    api.get("/landing/showcase").then((r) => setImages(r.data.images || [])).catch(() => setImages([]));
  }, []);

  const launchPlayground = () => {
    if (onPlay && images.length) onPlay(idx);
  };

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(t);
  }, [images]);

  if (!images.length) {
    // Skeleton while loading (or fallback to gradient card if the API 404's)
    return (
      <div
        className="aspect-[4/5] rounded-3xl relative overflow-hidden grain"
        style={{ background: "var(--brand-gradient)" }}
        data-testid="hero-carousel-loading"
      >
        <div className="absolute inset-0 flex items-center justify-center text-white/90 text-6xl">⚡🔋</div>
      </div>
    );
  }
  const active = images[idx];
  const IMAGE_HOST = process.env.REACT_APP_BACKEND_URL;
  const resolvedUrl = active.image_url?.startsWith("http")
    ? active.image_url
    : `${IMAGE_HOST}${active.image_url}`;
  return (
    <div className="relative" data-testid="hero-carousel">
      <div
        className="pm-card p-4 relative z-10 grain overflow-hidden"
        style={{ boxShadow: "0 30px 60px -20px rgba(15, 37, 71, 0.25)" }}
      >
        <div className="pm-badge mb-3" style={{ background: "var(--hl-yellow)", color: "var(--text)" }}>
          <Sparkles size={12} className="mr-1.5" /> Analogy of the moment
        </div>
        <h3 className="font-display text-2xl font-bold mb-3" data-testid="hero-carousel-caption">
          {active.caption}
        </h3>
        <div
          className="aspect-video rounded-2xl overflow-hidden mb-3 transition-opacity duration-500 relative group cursor-pointer"
          key={active.topic_id}
          onClick={launchPlayground}
          data-testid="hero-carousel-image-wrap"
        >
          <img
            src={resolvedUrl}
            alt={active.topic_name}
            className="w-full h-full object-cover"
            data-testid={`hero-carousel-img-${active.topic_id}`}
            loading="eager"
          />
          <div
            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: "rgba(15, 37, 71, 0.55)" }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
              style={{ background: "white", color: "#0F2547" }}
            >
              <Play size={14} fill="currentColor" /> Tap to explore this analogy
            </div>
          </div>
        </div>
        {active.analogy_snippet && (
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {active.analogy_snippet}
          </p>
        )}
        {/* Play CTA */}
        <button
          onClick={launchPlayground}
          className="pm-btn-primary w-full mt-3 inline-flex items-center justify-center gap-2"
          data-testid="hero-carousel-play-btn"
        >
          <Play size={14} fill="currentColor" /> Take the 4-topic analogy tour
        </button>
        {/* Dots */}
        <div className="mt-3 flex items-center justify-center gap-1.5" data-testid="hero-carousel-dots">
          {images.map((img, i) => (
            <button
              key={img.topic_id}
              onClick={() => setIdx(i)}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === idx ? 24 : 8,
                background: i === idx ? "var(--primary)" : "var(--border)",
              }}
              aria-label={`Show ${img.topic_name}`}
              data-testid={`hero-carousel-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const TEASER_COLORS = {
  Biology: "#10b981",
  Biochemistry: "#8b5cf6",
  "Gen Chem": "#f59e0b",
  Physics: "#3b82f6",
  Psychology: "#06b6d4",
};

function ConceptMapTeaser() {
  const [themes, setThemes] = useState([]);
  useEffect(() => {
    // Public GET — anonymous callers see the same free-plan gating as a
    // free user: exactly one theme comes back unlocked (the teaser).
    api.get("/concept-map").then((r) => setThemes(r.data.themes || [])).catch(() => {});
  }, []);
  if (themes.length === 0) return null;
  const teaser = themes.find((t) => !t.locked) || themes[0];
  const locked = themes.filter((t) => t.locked).slice(0, 4);
  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="landing-linkit-teaser">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="pm-badge mb-4" style={{ background: "var(--accent)", color: "white" }}>
            <Network size={12} className="mr-1.5" /> Link It
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-3">
            See how it all connects.
          </h2>
          <p className="text-base leading-relaxed mb-5" style={{ color: "var(--text-secondary)" }}>
            One MCAT idea usually lives across 3 subjects. PictureMed maps the biggest cross-discipline threads so you stop siloing biology, chemistry, and physics.
          </p>
          <Link
            to="/auth"
            className="pm-btn-accent inline-flex items-center gap-2"
            data-testid="landing-linkit-cta"
          >
            Start free — 1 theme unlocked instantly <ArrowRight size={16} />
          </Link>
        </div>

        <div className="lg:col-span-8 space-y-3">
          {/* Featured (unlocked) teaser theme */}
          <div className="pm-card p-5" data-testid="landing-linkit-featured">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="pm-badge" style={{ background: "var(--mastery)", color: "white" }}>
                <Sparkles size={11} className="mr-1" /> Free
              </span>
              <h3 className="font-display font-bold text-lg md:text-xl">{teaser.title}</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>{teaser.hook}</p>
            <div className="flex flex-wrap gap-2">
              {(teaser.nodes || []).map((n, i) => (
                <span
                  key={i}
                  className="text-xs px-3 py-1.5 rounded-full border"
                  style={{
                    borderColor: "var(--border)",
                    background: "var(--paper)",
                  }}
                >
                  <span
                    className="text-[10px] uppercase font-semibold tracking-widest mr-1.5"
                    style={{ color: TEASER_COLORS[n.discipline] || "var(--primary)" }}
                  >
                    {n.discipline}
                  </span>
                  {n.topic_name}
                </span>
              ))}
            </div>
            {teaser.takeaway && (
              <p
                className="text-sm mt-4 p-3 rounded-xl italic"
                style={{ background: "var(--hl-yellow)", color: "var(--text)" }}
              >
                → {teaser.takeaway}
              </p>
            )}
          </div>

          {/* Locked previews */}
          <div className="grid sm:grid-cols-2 gap-3" data-testid="landing-linkit-locked-grid">
            {locked.map((t) => (
              <div
                key={t.id}
                className="pm-card p-4 flex items-start gap-3 opacity-80"
                style={{ borderStyle: "dashed" }}
              >
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--muted)" }}
                >
                  <Lock size={14} style={{ color: "var(--text-muted)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm mb-0.5 truncate">{t.title}</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t.hook}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-center pt-2" style={{ color: "var(--text-muted)" }}>
            + {Math.max(0, themes.length - 1)} more cross-discipline threads on Plus
          </p>
        </div>
      </div>
    </section>
  );
}

// ---- Full Test Teaser: 3 interactive MCAT sample questions ----
const TEASER_QUESTIONS = [
  {
    id: "bio-1",
    section: "Bio/Biochem",
    sectionColor: "#10b981",
    passage:
      "A biochemist studies a mutant strain of yeast that cannot grow on glucose alone, but grows normally on pyruvate. Enzyme assays show wild-type activity for pyruvate dehydrogenase and every enzyme of the citric acid cycle. Glycolytic intermediates upstream of pyruvate accumulate in the mutant.",
    stem:
      "Which enzyme is most likely defective in the mutant strain?",
    choices: [
      { key: "A", text: "Hexokinase" },
      { key: "B", text: "Phosphofructokinase-1" },
      { key: "C", text: "Pyruvate kinase" },
      { key: "D", text: "Lactate dehydrogenase" },
    ],
    correct: "C",
    explanation:
      "Glycolytic intermediates pile up but growth on pyruvate is fine — so the block sits at the last committed step of glycolysis, converting PEP → pyruvate. That's pyruvate kinase.",
  },
  {
    id: "chem-1",
    section: "Chem/Phys",
    sectionColor: "#f59e0b",
    passage:
      "A rigid 2.0 L vessel contains 0.50 mol of an ideal gas at 300 K. The gas is heated at constant volume until the pressure doubles.",
    stem:
      "What is the final temperature of the gas?",
    choices: [
      { key: "A", text: "150 K" },
      { key: "B", text: "300 K" },
      { key: "C", text: "450 K" },
      { key: "D", text: "600 K" },
    ],
    correct: "D",
    explanation:
      "At constant V and n, PV = nRT reduces to P ∝ T. Doubling pressure doubles temperature: 300 K × 2 = 600 K.",
  },
  {
    id: "cars-1",
    section: "CARS",
    sectionColor: "#8b5cf6",
    passage:
      "The romantic view of the lone inventor persists in popular culture, but the historical record tells a subtler story. Nearly every celebrated breakthrough — the telephone, the airplane, the transistor — was preceded by a decade of overlapping work by rivals converging on the same problem. What we call \"invention\" is often the moment a public credit system catches up with a private consensus.",
    stem:
      "The author's primary purpose in this paragraph is to:",
    choices: [
      { key: "A", text: "argue that patent law fails to protect true innovators." },
      { key: "B", text: "complicate the myth that breakthroughs come from isolated geniuses." },
      { key: "C", text: "praise cooperation between competing inventors." },
      { key: "D", text: "trace the technical evolution of the transistor." },
    ],
    correct: "B",
    explanation:
      "The paragraph pushes back on the \"lone inventor\" narrative — showing invention as convergent, collaborative, and credit-driven. That's choice B.",
  },
];

function FullTestTeaser() {
  const [answers, setAnswers] = useState({}); // { [qid]: "A" }
  const [revealed, setRevealed] = useState({}); // { [qid]: true }

  const pick = (qid, key) => {
    if (revealed[qid]) return;
    setAnswers((a) => ({ ...a, [qid]: key }));
  };
  const submit = (qid) => {
    if (!answers[qid]) return;
    setRevealed((r) => ({ ...r, [qid]: true }));
  };
  const answeredCount = Object.keys(revealed).length;

  return (
    <section
      className="max-w-7xl mx-auto px-6 py-16"
      data-testid="landing-fulltest-teaser"
    >
      {/* Header row */}
      <div className="grid lg:grid-cols-12 gap-8 mb-10">
        <div className="lg:col-span-7">
          <div
            className="pm-badge mb-4"
            style={{ background: "var(--primary)", color: "white" }}
          >
            <FileText size={12} className="mr-1.5" /> Full Test Preview
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-3">
            Sit a real MCAT block — for free.
          </h2>
          <p
            className="text-base leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            Answer 3 sample questions from our 230-question full-length test.
            Get instant feedback and an explanation for each. Unlock the rest —
            plus timing, weak-area analysis, and section scoring — when you upgrade.
          </p>
        </div>
        <div className="lg:col-span-5 flex flex-wrap gap-2 items-start lg:justify-end">
          <span
            className="pm-badge"
            style={{ background: "var(--hl-blue)", color: "var(--primary)" }}
            data-testid="fulltest-teaser-meta-length"
          >
            <Timer size={12} className="mr-1.5" /> ~7.5 hrs · 230 questions
          </span>
          <span
            className="pm-badge"
            style={{ background: "var(--hl-yellow)", color: "var(--text)" }}
          >
            <Clock size={12} className="mr-1.5" /> Untimed · 1x · 1.5x · 2x
          </span>
          <span
            className="pm-badge"
            style={{ background: "var(--mastery)", color: "white" }}
          >
            <Target size={12} className="mr-1.5" /> 4 sections
          </span>
        </div>
      </div>

      {/* Questions grid */}
      <div
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="fulltest-teaser-questions"
      >
        {TEASER_QUESTIONS.map((q) => {
          const chosen = answers[q.id];
          const isRevealed = !!revealed[q.id];
          const isCorrect = chosen === q.correct;
          return (
            <div
              key={q.id}
              className="pm-card p-5 flex flex-col"
              data-testid={`fulltest-teaser-q-${q.id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded-full"
                  style={{
                    background: q.sectionColor + "22",
                    color: q.sectionColor,
                  }}
                >
                  {q.section}
                </span>
                <span
                  className="text-xs"
                  style={{ color: "var(--text-muted)" }}
                >
                  Sample question
                </span>
              </div>

              <p
                className="text-xs leading-relaxed mb-3 p-3 rounded-lg"
                style={{
                  background: "var(--paper)",
                  color: "var(--text-secondary)",
                }}
              >
                {q.passage}
              </p>

              <p className="text-sm font-semibold mb-3">{q.stem}</p>

              <div className="space-y-2 mb-3 flex-1">
                {q.choices.map((c) => {
                  const isPicked = chosen === c.key;
                  const isRight = c.key === q.correct;
                  let bg = "var(--paper)";
                  let border = "var(--border)";
                  let color = "var(--text)";
                  if (isRevealed && isRight) {
                    bg = "#10b98122";
                    border = "#10b981";
                  } else if (isRevealed && isPicked && !isRight) {
                    bg = "#ef444422";
                    border = "#ef4444";
                  } else if (isPicked && !isRevealed) {
                    bg = "var(--hl-blue)";
                    border = "var(--primary)";
                  }
                  return (
                    <button
                      key={c.key}
                      onClick={() => pick(q.id, c.key)}
                      disabled={isRevealed}
                      className="w-full text-left text-sm px-3 py-2 rounded-lg border transition-all flex items-start gap-2"
                      style={{
                        background: bg,
                        borderColor: border,
                        color,
                        cursor: isRevealed ? "default" : "pointer",
                      }}
                      data-testid={`fulltest-teaser-${q.id}-choice-${c.key}`}
                    >
                      <span className="font-bold shrink-0">{c.key}.</span>
                      <span className="flex-1">{c.text}</span>
                      {isRevealed && isRight && (
                        <CheckCircle2 size={16} style={{ color: "#10b981" }} />
                      )}
                      {isRevealed && isPicked && !isRight && (
                        <XCircle size={16} style={{ color: "#ef4444" }} />
                      )}
                    </button>
                  );
                })}
              </div>

              {!isRevealed ? (
                <button
                  onClick={() => submit(q.id)}
                  disabled={!chosen}
                  className="pm-btn-primary w-full text-sm disabled:opacity-50"
                  style={{ cursor: chosen ? "pointer" : "not-allowed" }}
                  data-testid={`fulltest-teaser-${q.id}-submit`}
                >
                  {chosen ? "Check answer" : "Pick an answer"}
                </button>
              ) : (
                <div
                  className="p-3 rounded-lg text-xs leading-relaxed"
                  style={{
                    background: isCorrect ? "#10b98115" : "var(--hl-yellow)",
                    color: "var(--text)",
                  }}
                  data-testid={`fulltest-teaser-${q.id}-explanation`}
                >
                  <p className="font-bold mb-1">
                    {isCorrect ? "✓ Nice — that's correct." : `✗ The answer is ${q.correct}.`}
                  </p>
                  <p style={{ color: "var(--text-secondary)" }}>{q.explanation}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Locked "next question" tile + upgrade CTA */}
      <div className="mt-6 grid lg:grid-cols-12 gap-4 items-stretch">
        <div
          className="lg:col-span-7 pm-card p-5 relative overflow-hidden"
          style={{ borderStyle: "dashed" }}
          data-testid="fulltest-teaser-locked-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded-full"
              style={{ background: "var(--muted)", color: "var(--text-muted)" }}
            >
              Question 4 of 230
            </span>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              Psych/Soc · locked
            </span>
          </div>
          <div
            className="space-y-2 select-none"
            style={{ filter: "blur(4px)", userSelect: "none" }}
            aria-hidden
          >
            <p className="text-sm">
              A researcher administers a Likert-scale survey measuring social
              conformity across three cohorts. She then compares mean scores
              using a one-way ANOVA...
            </p>
            <p className="text-sm font-semibold">
              Which of the following best describes the operational definition
              used in this study?
            </p>
            <div className="space-y-1">
              <div
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--border)" }}
              >
                A. The abstract construct of conformity itself.
              </div>
              <div
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--border)" }}
              >
                B. The Likert-scale composite score used to quantify it.
              </div>
            </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="flex flex-col items-center gap-2 px-6 py-4 rounded-2xl"
              style={{
                background: "rgba(255,255,255,0.92)",
                boxShadow: "0 10px 40px -10px rgba(15,37,71,0.35)",
              }}
            >
              <Lock size={20} style={{ color: "var(--primary)" }} />
              <p className="font-display font-bold text-sm">
                +226 more questions on Plus
              </p>
            </div>
          </div>
        </div>

        <div
          className="lg:col-span-5 pm-card p-5 flex flex-col justify-between"
          style={{ background: "var(--primary)", color: "white" }}
        >
          <div>
            <p
              className="text-xs uppercase font-bold tracking-widest mb-2"
              style={{ color: "var(--hl-yellow)" }}
            >
              Unlock the full 230
            </p>
            <h3 className="font-display font-bold text-2xl leading-tight mb-2">
              {answeredCount === 3
                ? "You've got the pattern — now try the real thing."
                : "Free gets you 1 half-length test."}
            </h3>
            <p className="text-sm opacity-90 mb-4">
              Plus gives you unlimited full-length tests with timing modes,
              per-section scores, and weak-area retargeting.
            </p>
          </div>
          <Link
            to="/auth?next=/app/plans"
            className="pm-btn-accent inline-flex items-center justify-center gap-2 text-sm"
            data-testid="fulltest-teaser-upgrade-cta"
          >
            Upgrade to Plus — $17.99/mo <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

const PLAN_META = {
  free: {
    icon: Sparkles,
    tint: "#6a7570",
    tag: "Start here",
    blurb: "Kick the tires with a lifetime free credit grant.",
  },
  plus: {
    icon: Zap,
    tint: "#4f7942",
    tag: "Most popular",
    blurb: "Every subject, every practice test, refreshed monthly.",
  },
  pro: {
    icon: Crown,
    tint: "#c98a3b",
    tag: "Power users",
    blurb: "Priority queue + community library + early features.",
  },
};

function PricingTeaser() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    // Public endpoint — same source of truth as /app/plans.
    api.get("/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);
  if (!plans.length) return null;
  return (
    <section
      className="max-w-7xl mx-auto px-6 py-20"
      id="pricing"
      data-testid="landing-pricing"
    >
      <div className="max-w-2xl mx-auto text-center mb-10">
        <div
          className="pm-badge mb-4 inline-flex"
          style={{ background: "var(--hl-yellow)", color: "var(--text)" }}
        >
          <Sparkles size={12} className="mr-1.5" /> Simple, honest pricing
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-3">
          Pick a plan. Cancel any time.
        </h2>
        <p
          className="text-base leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Every plan includes the built-in analogy + story explanations,
          spaced-repetition homework queue, and CARS passage analyzer. The
          searchable Image Library and premium features unlock on Plus. Paid
          plans refresh your Open Topic Credits every month.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5" data-testid="landing-pricing-grid">
        {plans.map((p) => {
          const meta = PLAN_META[p.id] || PLAN_META.free;
          const Icon = meta.icon;
          const isFeatured = p.id === "plus";
          const priceDollars = (p.price_cents / 100).toFixed(p.price_cents % 100 === 0 ? 0 : 2);
          return (
            <div
              key={p.id}
              className="pm-card p-6 flex flex-col relative"
              style={{
                borderColor: isFeatured ? meta.tint : "var(--border)",
                borderWidth: isFeatured ? 2 : 1,
                background: isFeatured ? `${meta.tint}0a` : "var(--paper)",
                boxShadow: isFeatured
                  ? `0 25px 50px -20px ${meta.tint}55`
                  : undefined,
              }}
              data-testid={`landing-plan-${p.id}`}
            >
              {isFeatured && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 pm-badge text-[10px] uppercase tracking-widest"
                  style={{
                    background: meta.tint,
                    color: "white",
                    padding: "4px 12px",
                  }}
                  data-testid={`landing-plan-${p.id}-badge`}
                >
                  {meta.tag}
                </div>
              )}

              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: `${meta.tint}22` }}
                >
                  <Icon size={16} style={{ color: meta.tint }} />
                </div>
                <div>
                  <div
                    className="text-[10px] uppercase tracking-widest font-bold"
                    style={{ color: meta.tint }}
                  >
                    {p.name}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {meta.blurb}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold">
                    ${priceDollars}
                  </span>
                  {p.price_cents > 0 && (
                    <span
                      className="text-sm"
                      style={{ color: "var(--text-muted)" }}
                    >
                      /month
                    </span>
                  )}
                </div>
                <div
                  className="text-xs mt-1"
                  style={{ color: "var(--text-muted)" }}
                >
                  {p.credits_one_time
                    ? `${p.credits_per_period} one-time Open Topic Credits`
                    : `${p.credits_per_period} Open Topic Credits refreshed monthly`}
                </div>
              </div>

              <ul
                className="space-y-2 text-sm mb-5 flex-1"
                data-testid={`landing-plan-${p.id}-features`}
              >
                {p.features.slice(0, 5).map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check
                      size={14}
                      style={{ color: meta.tint, marginTop: 3, flexShrink: 0 }}
                    />
                    <span style={{ color: "var(--text-secondary)" }}>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={p.id === "free" ? "/auth" : "/auth?next=/app/plans"}
                className={isFeatured ? "pm-btn-primary" : "pm-btn-secondary"}
                style={
                  isFeatured
                    ? { background: meta.tint, borderColor: meta.tint }
                    : { color: meta.tint, borderColor: meta.tint }
                }
                data-testid={`landing-plan-${p.id}-cta`}
              >
                {p.id === "free" ? "Start free" : `Get ${p.name}`}
              </Link>
            </div>
          );
        })}
      </div>

      <p
        className="text-xs text-center mt-6"
        style={{ color: "var(--text-muted)" }}
      >
        Need more credits without upgrading? Grab a one-off <Link to="/auth" className="underline" style={{ color: "var(--primary)" }}>credit pack</Link> after signup — 5, 10, or 20 credits.
      </p>
    </section>
  );
}

export default function Landing() {
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  const [playgroundStart, setPlaygroundStart] = useState(0);
  const [showcase, setShowcase] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/landing/showcase").then((r) => setShowcase(r.data.images || [])).catch(() => {});
  }, []);

  const openPlayground = (startIdx = 0) => {
    setPlaygroundStart(startIdx);
    setPlaygroundOpen(true);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="pm-badge mb-6" style={{ background: "var(--hl-blue)", color: "var(--primary)" }}>
            <Sparkles size={12} className="mr-1.5" /> AI-powered analogies + images
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight mb-6">
            Learn the MCAT the way<br />
            your brain <span style={{ color: "var(--accent)" }}>actually</span> remembers.
          </h1>
          <p className="text-lg leading-relaxed max-w-xl mb-8" style={{ color: "var(--text-secondary)" }}>
            Every hard science concept — Biology, Biochem, O-Chem, Physics, Psych/Soc — taught through vivid analogies, funny cartoon images, and stories that stick. Plus a full question bank, CARS passage analyzer, spaced-repetition homework, and dimensional analysis practice.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/auth" className="pm-btn-primary flex items-center gap-2 text-base" data-testid="hero-start-btn">
              Start studying free <ArrowRight size={18} />
            </Link>
            <button
              onClick={() => openPlayground(0)}
              className="pm-btn-secondary flex items-center gap-2 text-base"
              data-testid="hero-demo-btn"
              disabled={showcase.length === 0}
            >
              <Play size={16} fill="currentColor" /> Try a 60-second demo
            </button>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm" style={{ color: "var(--text-muted)" }}>
            <div>✓ All AAMC subjects</div>
            <div>✓ CARS passage breakdown</div>
            <div>✓ Spaced repetition</div>
          </div>
        </div>

        {/* Right visual — rotating hero carousel */}
        <div className="lg:col-span-5">
          <HeroCarousel onPlay={openPlayground} />
        </div>
      </section>

      {/* Full Test Teaser — high-intent preview right after hero */}
      <FullTestTeaser />

      {/* Feature strip — How it Works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6 scroll-mt-20" data-testid="landing-how-it-works">
        <div className="md:col-span-3 mb-2 text-center">
          <div className="pm-badge mb-3 inline-flex" style={{ background: "var(--hl-yellow)" }}>
            <Sparkles size={12} className="mr-1.5" /> How it works
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Three habits that make MCAT content stick.</h2>
        </div>
        {[
          { icon: Brain, title: "Analogies + Stories", desc: "Every topic paired with a vivid analogy, a memorable story, and a funny cartoon image generated on demand." },
          { icon: BookOpenCheck, title: "Question Bank + CARS", desc: "Challenging MCQs across all AAMC content categories. Real-length CARS passages with AI breakdowns." },
          { icon: Target, title: "Spaced Repetition", desc: "Weakest topics surface first. Homework queue reminds you before you forget." },
        ].map((f, i) => (
          <div key={i} className="pm-card pm-card-hover p-6" data-testid={`feature-card-${i}`}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--hl-blue)" }}>
              <f.icon size={20} style={{ color: "var(--primary)" }} />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{f.desc}</p>
          </div>
        ))}
      </section>

      <ConceptMapTeaser />

      <PricingTeaser />

      <footer className="max-w-7xl mx-auto px-6 py-10 text-sm border-t space-y-2" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }} data-testid="landing-footer">
        <div>© {new Date().getFullYear()} PictureMed. Study smarter, not harder.</div>
        <div className="text-xs leading-relaxed max-w-3xl" data-testid="landing-aamc-disclaimer">
          PictureMed is an independent study tool and is not affiliated with,
          endorsed by, or sponsored by the Association of American Medical
          Colleges (AAMC). MCAT® is a registered trademark of the AAMC. All
          practice content is modeled on the exam's publicly documented timing,
          section structure, and passage-based format.
        </div>
      </footer>

      {playgroundOpen && showcase.length > 0 && (
        <AnalogyPlayground
          topics={showcase}
          startIndex={playgroundStart}
          onClose={() => setPlaygroundOpen(false)}
          onSignup={() => nav("/auth")}
        />
      )}
    </div>
  );
}
